import { useState } from "react";
import {
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  RefreshCw,
  EyeOff,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAIAssist, type AIError } from "@/hooks/useAIAssist";

interface JoinAIPrepProps {
  meetingTitle?: string | null;
  meetingCode?: string;
}

interface PrepResult {
  context?: string;
  questions?: string[];
}

export function JoinAIPrep({ meetingTitle, meetingCode }: JoinAIPrepProps) {
  const { call, loading, lastError, clearError } = useAIAssist();
  const [open, setOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [result, setResult] = useState<PrepResult | null>(null);

  const isLoading = loading === "join_context";

  const run = async () => {
    setOpen(true);
    clearError();
    const data = await call<any>({
      mode: "join_context",
      intent: "prepare to join",
      context: `Meeting: ${meetingTitle || "(untitled)"} (code ${meetingCode || "?"})`,
    });
    if (data) {
      setResult({
        context: data.context || data.summary || "",
        questions: data.questions || data.suggested_questions || [],
      });
    }
  };

  const errorCopy = (err: AIError) => {
    switch (err.kind) {
      case "rate_limit":
        return "AI is busy right now. Wait a few seconds and retry.";
      case "credits":
        return "AI credits exhausted. Add credits in workspace settings.";
      case "network":
        return "Couldn't reach the AI service. Check your connection.";
      default:
        return err.message || "Something went wrong generating suggestions.";
    }
  };

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-primary shrink-0" />
          <p className="text-sm font-medium truncate">Join prepared, not confused</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={result ? () => setOpen((p) => !p) : run}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Preparing
            </>
          ) : result ? (
            open ? (
              <>
                <ChevronUp className="h-3.5 w-3.5 mr-1" /> Hide
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5 mr-1" /> Show
              </>
            )
          ) : (
            "Prep with AI"
          )}
        </Button>
      </div>

      {/* Toggle for AI-generated suggestions */}
      {(result || open) && (
        <div className="flex items-center justify-between rounded-md bg-background/60 px-2 py-1.5">
          <Label
            htmlFor="ai-suggestions-toggle"
            className="flex items-center gap-1.5 text-[12px] text-muted-foreground cursor-pointer"
          >
            {showSuggestions ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Show AI-generated suggestions
          </Label>
          <Switch
            id="ai-suggestions-toggle"
            checked={showSuggestions}
            onCheckedChange={setShowSuggestions}
          />
        </div>
      )}

      {/* Error state */}
      {lastError && !isLoading && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5 space-y-2 animate-fade-in">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-[12px] text-foreground/90 leading-relaxed">{errorCopy(lastError)}</p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={clearError}>
              Dismiss
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[12px]"
              onClick={run}
              disabled={lastError.kind === "credits"}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              {lastError.kind === "rate_limit" ? "Try again" : "Retry"}
            </Button>
          </div>
        </div>
      )}

      {/* Result */}
      {open && result && showSuggestions && !lastError && (
        <div className="space-y-2 text-[13px] animate-fade-in">
          {result.context && <p className="leading-relaxed text-foreground/90">{result.context}</p>}
          {result.questions && result.questions.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Questions to ask
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                {result.questions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {open && result && !showSuggestions && !lastError && (
        <p className="text-[11px] text-muted-foreground italic">
          Suggestions hidden. Toggle on to view.
        </p>
      )}

      {!result && !isLoading && !lastError && (
        <p className="text-[11px] text-muted-foreground">
          Get a 2-line context brief and smart questions before you join.
        </p>
      )}
    </div>
  );
}

export default JoinAIPrep;
